import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { ImprovedWithAiBadge } from "../../../components/ImprovedWithAiBadge";

import { useAskPerformYardStore } from "../../../containers/AskPerformYard/AskPerformYardStore";

/** Marks the nodes this component owns so it can clean up the ones it no longer needs. */
const MOUNT_CLASS = "js-ai-improved-badge";

/**
 * The element the badge sits above. Ordered widest-first: a text question's wrapper rather than the
 * textarea inside it, so the badge lands above the whole answer rather than between its parts.
 */
const findAnswerAnchor = (block: HTMLElement): HTMLElement | null =>
	block.querySelector<HTMLElement>(".question-text-area, .answer-list, .box, textarea, input.isanumber");

/**
 * Anchors that are, or wrap, a field the author types into — a long answer's textarea wrapper, one of
 * a multiline answer's bullet rows, a numeric input. Those get 4px of air so the badge doesn't sit on
 * the field's top border. A choice list (`.answer-list`, shared by multiple-choice, multiple-select,
 * and rating scale) has no border to crowd, so it stays flush.
 */
const TYPED_INPUT_ANCHORS = ".question-text-area, .box, textarea, input.isanumber";

/** 4px above the badge, separating it from the question title it sits under. Every anchor gets this. */
const BADGE_TOP_GAP_CLASS = "mt-1";

/** 4px between the badge and the input below it — one class, so the toggle below can add or drop it. */
const ANCHOR_GAP_CLASS = "mb-1";

/**
 * "Improved with AI" above each answer the Form Authoring Coach rewrote (PY-13212).
 *
 * The review form is the legacy template, so the badge can't be rendered inline the way the goal
 * wizard and form builder do it. Instead the mount point is created next to the answer and the shared
 * component is portalled into it — one badge implementation, three surfaces.
 */
export const ImprovedAnswerBadges: React.FC = () => {
	const improvedIndexes = useAskPerformYardStore((state) => state.aiImprovedAnswerIndexes);
	const clearImproved = useAskPerformYardStore((state) => state.clearAnswerImprovedWithAi);
	const [mounts, setMounts] = useState<Array<{ index: number; element: HTMLElement }>>([]);

	useEffect(() => {
		const next: Array<{ index: number; element: HTMLElement }> = [];

		for (const index of improvedIndexes) {
			const block = document.querySelector<HTMLElement>(`#report .question-block[data-index="${index}"]`);
			const anchor = block ? findAnswerAnchor(block) : null;
			if (!block || !anchor?.parentElement) continue;

			let element = block.querySelector<HTMLElement>(`.${MOUNT_CLASS}`);
			if (!element) {
				element = document.createElement("div");
				element.className = `${MOUNT_CLASS} ${BADGE_TOP_GAP_CLASS} hide-in-pdf`;
				anchor.parentElement.insertBefore(element, anchor);
			}
			// Toggled rather than set once: a reused mount can end up above a different anchor when the
			// answer it marks is re-rendered by the legacy page.
			element.classList.toggle(ANCHOR_GAP_CLASS, anchor.matches(TYPED_INPUT_ANCHORS));
			next.push({ index, element });
		}

		// Drop mounts whose answer is no longer flagged — the author edited it, or a new chat started.
		document.querySelectorAll<HTMLElement>(`#report .${MOUNT_CLASS}`).forEach((element) => {
			if (!next.some((mount) => mount.element === element)) element.remove();
		});

		setMounts(next);
	}, [improvedIndexes]);

	useEffect(() => {
		const report = document.getElementById("report");
		if (!report) return;

		const clearForTarget = (target: EventTarget | null): void => {
			const block = target instanceof Element ? target.closest<HTMLElement>(".question-block") : null;
			const index = Number(block?.dataset.index);
			if (Number.isInteger(index)) clearImproved(index);
		};

		// Typing is the AC's trigger; for a question with no text input, changing the selection is the
		// equivalent. Delegated from #report so questions added after mount are covered too.
		const onInput = (event: Event): void => clearForTarget(event.target);
		const onChoiceClick = (event: Event): void => {
			if (event.target instanceof Element && event.target.closest(".answer-choice")) {
				clearForTarget(event.target);
			}
		};

		report.addEventListener("input", onInput);
		report.addEventListener("click", onChoiceClick);
		return () => {
			report.removeEventListener("input", onInput);
			report.removeEventListener("click", onChoiceClick);
		};
	}, [clearImproved]);

	return (
		<>
			{mounts.map(({ index, element }) => (
				<React.Fragment key={index}>{createPortal(<ImprovedWithAiBadge />, element)}</React.Fragment>
			))}
		</>
	);
};

export default ImprovedAnswerBadges;
